import type { CSSProperties } from 'react';

/**
 * ProjectListPage 頁面層 styles(從 `ProjectListPage` 抽出的純結構重構,行為不變)。
 * 卡片 / 列 / kebab / 刪除框的 styles 跟著各自元件檔;這裡只收頁面骨架
 * (breadcrumb / header / filter bar / grid / list / dashed 建立入口 / 狀態列)。
 */

// R412 — 與另一張列表頁逐字相同的外框樣式改由共用模組提供(本檔 import 路徑不變)。
export {
  breadcrumbStyle,
  breadcrumbLinkStyle,
  breadcrumbSepStyle,
  breadcrumbCurrentStyle,
  headerStyle,
  titleStyle,
  filterBarStyle,
  searchIconStyle,
  searchInputStyle,
  sortSelectStyle,
  dashedTilePlusBoxStyle,
  primaryBtnStyle,
  errorBannerStyle,
  loadingStyle,
} from '@/components/listPageStyles';

export const subtitleStyle: CSSProperties = {
  margin: '2px 0 0',
  color: 'var(--ink-500)',
  fontSize: 14,
  lineHeight: 1.6,
};

/**
 * R397 — 框線不寫 inline:聚焦變色走全站 `.hai-searchbox`(global.css),inline 的
 * border 會把 `:focus-within` 蓋掉。容器套 class + 這份版型。
 */
export const searchBoxStyle: CSSProperties = {
  flex: '0 0 380px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  background: 'var(--ink-900)',
  borderRadius: 'var(--r-sm)',
};

export const countLineStyle: CSSProperties = {
  marginBottom: 16,
  fontSize: 11.5,
  color: 'var(--ink-500)',
};

export const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 16,
};

export const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

// 網格 / 列表分段切換容器(對齊 M-12 mockup line 198-205 的 slate-100 segmented control)
export const viewToggleStyle: CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  padding: 2,
  background: 'var(--ink-800)',
  borderRadius: 'var(--r-sm)',
};

export function viewToggleBtnStyle(active: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px 9px',
    border: 'none',
    borderRadius: 6,
    background: active ? 'var(--ink-900)' : 'transparent',
    // D3 `.vseg button.on` = 白底 + teal-700 圖示(CIS 選取態文字 = teal),不是深灰
    color: active ? 'var(--concept-fg)' : 'var(--ink-500)',
    boxShadow: active ? 'var(--shadow-sm)' : 'none',
    cursor: 'pointer',
    transition: 'background 0.12s, color 0.12s',
  };
}

export const dashedRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '12px 16px',
  border: '1px dashed var(--hairline-strong)',
  borderRadius: 'var(--r-sm)',
  // R293:三個建立入口從 `<Link to="/new">` 改成開燈箱的 `<button>` —— button 不會
  // 繼承字體、也自帶 UA 底色,所以這兩行是必要的(不是裝飾)。
  background: 'none',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  textAlign: 'left',
  color: 'var(--ink-500)',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'border-color 0.12s, color 0.12s',
};

/** 圓框裡放 `<HaiIcon name="plus">`(R393:原本是字元「+」—— CIS 禁字元圖示)。 */
export const dashedRowPlusBoxStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 999,
  background: 'var(--ink-850)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
  flexShrink: 0,
};

export const dashedTileStyle: CSSProperties = {
  // R293:同 `dashedRowStyle` —— 現在掛在 `<button>` 上。
  background: 'none',
  fontFamily: 'inherit',
  border: '2px dashed var(--hairline-strong)',
  borderRadius: 'var(--r-lg)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: '48px 24px',
  color: 'var(--ink-500)',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'border-color 0.12s, color 0.12s',
  minHeight: 240,
};

export const dashedTileLabelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
};

